// MyDashboardOverview — "My Dashboard" hero on the Home tab.
//
// Three stacked blocks, matching the user's screenshot brief:
//
//   1. Two stat circles
//      ┌────────────────┐    ┌────────────────┐
//      │  Total Orders  │    │  Total Spends  │
//      │      N         │    │     ₹N         │
//      │   (this month) │    │ (Current Month)│
//      └────────────────┘    └────────────────┘
//
//   2. Month View calendar (current month). Each day a user ordered shows
//      brand-coloured dot(s) — Blinkit yellow, Zepto purple, Swiggy orange,
//      Zomato red, etc. Multiple orders from different brands on the same
//      day overlap as small offset circles.
//
//   3. Day detail panel — appears below the calendar when a day with orders
//      is tapped. Lists each order: platform pill + amount + items.
//
// Data source: GET /api/orders/by-month?year=Y&month=M (authenticated).

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

// Brand colours — derived from each platform's marketing palette. Keeping
// these in one place so the calendar dots, day-detail pills, and any future
// per-brand chart all stay consistent.
const PLATFORM_COLORS = {
  blinkit: "#F8CB46",
  zepto: "#8B5CF6",
  swiggy_instamart: "#FC8019",
  swiggy: "#FC8019",
  instamart: "#FC8019",
  zomato: "#E23744",
  bigbasket: "#84C225",
  dunzo: "#00D4AA",
  unknown: "#888888",
};

const PLATFORM_LABELS = {
  blinkit: "Blinkit",
  zepto: "Zepto",
  swiggy_instamart: "Swiggy Instamart",
  swiggy: "Swiggy",
  instamart: "Instamart",
  zomato: "Zomato",
  bigbasket: "BigBasket",
  dunzo: "Dunzo",
  unknown: "Other",
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(Number(n) || 0));

const platformLabel = (p) =>
  PLATFORM_LABELS[(p || "").toLowerCase()] || p || "Other";

const platformColor = (p) =>
  PLATFORM_COLORS[(p || "").toLowerCase()] || PLATFORM_COLORS.unknown;

/**
 * Build the 6×7 calendar matrix for the given (year, month) — Indian
 * convention starts week on Sunday. Leading/trailing cells outside the month
 * are `null` so the grid renders proper blanks.
 */
function buildCalendarMatrix(year, month) {
  const first = new Date(year, month - 1, 1);
  const startWeekday = first.getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  // Slice into weeks
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export default function MyDashboardOverview() {
  const { user } = useAuth();
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) return undefined;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get("/orders/by-month", {
          params: { year, month },
        });
        if (!cancelled) setData(res?.data || null);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail || e?.message || "Could not load orders.",
          );
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, year, month]);

  const weeks = useMemo(() => buildCalendarMatrix(year, month), [year, month]);
  const daysMap = data?.days || {};
  const todayDay =
    today.getFullYear() === year && today.getMonth() + 1 === month
      ? today.getDate()
      : null;

  const selectedOrders = selectedDay ? daysMap[String(selectedDay)] || [] : [];

  // ── Sign-in CTA ──────────────────────────────────────────────────
  if (!user) {
    return (
      <section
        data-testid="my-dashboard-overview-signed-out"
        className="cartiq-card"
      >
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
          — My Dashboard
        </div>
        <h2 className="font-display uppercase text-2xl mb-2">
          Sign in to see your month at a glance
        </h2>
        <p className="text-sm text-[#888]">
          Once you sign in, this section shows your total orders, total
          spend so far this month, and a colour-coded calendar of every order.
        </p>
      </section>
    );
  }

  const monthLabel = `${MONTH_NAMES[month - 1]} ${year}`;
  const totalOrders = data?.totalOrders ?? 0;
  const totalSpend = data?.totalSpend ?? 0;

  return (
    <section
      data-testid="my-dashboard-overview"
      className="space-y-4 md:space-y-6"
    >
      {/* ── Two stat circles ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 md:gap-6">
        <StatCircle
          testid="stat-total-orders"
          label="Total Orders"
          subLabel="Current Month"
          value={loading ? "…" : String(totalOrders)}
          accent="#FF6B00"
          tone="warm"
        />
        <StatCircle
          testid="stat-total-spends"
          label="Total Spends"
          subLabel="Current Month"
          value={loading ? "…" : formatINR(totalSpend)}
          accent="#1D9E75"
          tone="cool"
        />
      </div>

      {/* ── Calendar ────────────────────────────────────────────────── */}
      <div className="cartiq-card" data-testid="month-view">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
              — Section · Month view
            </div>
            <h3 className="font-display uppercase text-xl">{monthLabel}</h3>
          </div>
          <MonthNav
            year={year}
            month={month}
            onPrev={() => {
              setSelectedDay(null);
              if (month === 1) { setMonth(12); setYear((y) => y - 1); }
              else setMonth((m) => m - 1);
            }}
            onNext={() => {
              setSelectedDay(null);
              if (month === 12) { setMonth(1); setYear((y) => y + 1); }
              else setMonth((m) => m + 1);
            }}
          />
        </div>

        {/* Brand legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-2 mb-3 text-xs">
          {Object.entries(PLATFORM_LABELS)
            .filter(([key]) => !["instamart", "swiggy"].includes(key))
            .map(([key, label]) => (
              <span
                key={key}
                className="inline-flex items-center gap-1.5 text-[#888]"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: PLATFORM_COLORS[key] }}
                />
                {label}
              </span>
            ))}
        </div>

        {error && (
          <div className="text-sm text-[#E24B4A] mb-3">{error}</div>
        )}

        {/* Day-of-week header */}
        <div className="grid grid-cols-7 text-center text-[10px] uppercase tracking-widest text-[#666] font-bold mb-2">
          {DAY_NAMES.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1.5 md:gap-2">
          {weeks.flat().map((day, idx) => (
            <CalendarCell
              key={idx}
              day={day}
              orders={day ? daysMap[String(day)] || [] : []}
              isToday={day === todayDay}
              isSelected={day === selectedDay}
              onClick={() => {
                if (!day) return;
                const hasOrders = (daysMap[String(day)] || []).length > 0;
                setSelectedDay(hasOrders && day !== selectedDay ? day : null);
              }}
            />
          ))}
        </div>
      </div>

      {/* ── Day detail panel ────────────────────────────────────────── */}
      {selectedDay && (
        <div
          className="cartiq-card"
          data-testid={`day-detail-${selectedDay}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#378ADD] font-bold mb-1">
                — {MONTH_NAMES[month - 1]} {selectedDay}, {year}
              </div>
              <h3 className="font-display uppercase text-xl">
                {selectedOrders.length}{" "}
                {selectedOrders.length === 1 ? "order" : "orders"} this day
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setSelectedDay(null)}
              className="text-xs uppercase tracking-widest text-[#888] hover:text-[#F0EDE8]"
              data-testid="day-detail-close"
            >
              Close ✕
            </button>
          </div>

          <div className="space-y-3">
            {selectedOrders.map((order, i) => (
              <OrderRow key={`${order.id}-${i}`} order={order} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ── Pieces ─────────────────────────────────────────────────────────────

function StatCircle({ testid, label, subLabel, value, accent, tone }) {
  const bgTint =
    tone === "warm"
      ? "linear-gradient(135deg, rgba(255,107,0,0.10), rgba(255,107,0,0.02))"
      : "linear-gradient(135deg, rgba(29,158,117,0.10), rgba(29,158,117,0.02))";
  return (
    <div
      className="cartiq-card flex flex-col items-center text-center py-6"
      data-testid={testid}
    >
      <div
        className="w-28 h-28 md:w-36 md:h-36 rounded-full flex items-center justify-center mb-3"
        style={{
          background: bgTint,
          border: `2.5px solid ${accent}`,
          boxShadow: `0 0 0 6px ${accent}10`,
        }}
      >
        <div
          className="font-mono-num font-extrabold tracking-tight"
          style={{ color: accent, fontSize: "clamp(20px, 4.4vw, 32px)" }}
        >
          {value}
        </div>
      </div>
      <div className="text-sm font-bold uppercase tracking-wider text-[#F0EDE8]">
        {label}
      </div>
      <div className="text-[11px] uppercase tracking-widest text-[#888] mt-0.5">
        {subLabel}
      </div>
    </div>
  );
}

function MonthNav({ year, month, onPrev, onNext }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onPrev}
        className="w-8 h-8 rounded border border-[#2A2A35] hover:border-[#FF6B00] text-[#F0EDE8] text-sm"
        data-testid="month-nav-prev"
        aria-label="Previous month"
      >
        ‹
      </button>
      <button
        type="button"
        onClick={onNext}
        className="w-8 h-8 rounded border border-[#2A2A35] hover:border-[#FF6B00] text-[#F0EDE8] text-sm"
        data-testid="month-nav-next"
        aria-label="Next month"
      >
        ›
      </button>
    </div>
  );
}

function CalendarCell({ day, orders, isToday, isSelected, onClick }) {
  // Empty cell (leading/trailing blank)
  if (!day) {
    return <div className="aspect-square" aria-hidden />;
  }
  const has = orders.length > 0;
  // Pull up to 4 unique brand colors for the day (rest collapse into "+N")
  const brandColors = [];
  const seen = new Set();
  for (const o of orders) {
    const key = (o.platform || "unknown").toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    brandColors.push(platformColor(key));
    if (brandColors.length >= 4) break;
  }
  const overflow = orders.length - brandColors.length;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!has}
      data-testid={`cal-day-${day}`}
      className={[
        "aspect-square rounded-md border flex flex-col items-center justify-center gap-1 relative transition-all",
        has
          ? "border-[#2A2A35] hover:border-[#FF6B00] cursor-pointer"
          : "border-transparent cursor-default",
        isSelected ? "ring-2 ring-[#FF6B00] border-[#FF6B00] bg-[#FF6B0010]" : "",
        isToday && !isSelected ? "border-[#378ADD]" : "",
      ].join(" ")}
    >
      <div
        className={[
          "text-xs font-mono-num font-bold",
          isToday ? "text-[#378ADD]" : "text-[#F0EDE8]",
        ].join(" ")}
      >
        {day}
      </div>
      {has && (
        <div className="flex items-center -space-x-1.5">
          {brandColors.map((c, i) => (
            <span
              key={i}
              className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full border border-[#161620]"
              style={{ background: c }}
              aria-hidden
            />
          ))}
          {overflow > 0 && (
            <span className="text-[9px] font-bold text-[#888] ml-1.5">
              +{overflow}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

function OrderRow({ order }) {
  const color = platformColor(order.platform);
  const label = platformLabel(order.platform);
  return (
    <div
      className="border border-[#2A2A35] rounded-md p-3 md:p-4"
      data-testid={`order-row-${order.platform}`}
    >
      <div className="flex items-center justify-between mb-2 gap-3">
        <span
          className="inline-flex items-center gap-2 px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider"
          style={{
            background: `${color}18`,
            border: `1px solid ${color}55`,
            color: color,
          }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: color }}
            aria-hidden
          />
          {label}
        </span>
        <span className="font-mono-num text-lg font-bold text-[#F0EDE8]">
          {formatINR(order.amount)}
        </span>
      </div>

      {order.items && order.items.length > 0 ? (
        <ul className="space-y-1.5 text-sm" data-testid="order-items">
          {order.items.slice(0, 8).map((it, idx) => (
            <li
              key={idx}
              className="flex items-center justify-between gap-3 text-[#F0EDE8]"
            >
              <span className="truncate">
                {it.qty && it.qty !== "1" ? `${it.qty} × ` : ""}
                {it.name}
              </span>
              <span className="text-[#888] font-mono-num text-xs shrink-0">
                {it.price ? formatINR(it.price) : ""}
              </span>
            </li>
          ))}
          {order.items.length > 8 && (
            <li className="text-[11px] uppercase tracking-widest text-[#666]">
              + {order.items.length - 8} more items
            </li>
          )}
        </ul>
      ) : (
        <div className="text-xs text-[#666] italic">
          {order.source === "webview"
            ? "Auto-captured from in-app browser — line items not extracted."
            : "No item details available."}
        </div>
      )}
    </div>
  );
}
